import React, { useState } from "react";
import ButtonComponent from "../../../atoms/Button/Button";
import InputField from "./InputField";
import SelectField from "./SelectField";
import { uploadImageToCloudinary } from "../../../../app/helper/functions";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

const Roles = [
  { label: "Frontend Developer", value: "Frontend Developer" },
  { label: "Backend Developer", value: "Backend Developer" },
  { label: "Graphic Designer", value: "Graphic Designer" },
  { label: "Product Manager", value: "Product Manager" },
];

const ApplyForm = () => {
   let location = useLocation()
  const [formData, setFormData] = useState({
    fname: "",
    lname: "",
    email: "",
    number: "",
    role: location.state || "",
    qualification: "",
    experience: "",
    resume: null as File | null,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const navigator = useNavigate()

  console.log(location)

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    // First Name
    if (!formData.fname.trim()) {
      newErrors.fname = "First name is required.";
    }

    // Last Name
    if (!formData.lname.trim()) {
      newErrors.lname = "Last name is required.";
    }

    // Email
    if (!formData.email) {
      newErrors.email = "Email is required.";
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Phone Number (exactly 10 digits)
    if (!formData.number) {
      newErrors.number = "Phone number is required.";
    } else if (!/^\d{10}$/.test(formData.number)) {
      newErrors.number = "Phone number must be exactly 10 digits.";
    }

    // Role
    if (!formData.role) {
      newErrors.role = "Please select a role.";
    }

    // Qualification
    if (!formData.qualification.trim()) {
      newErrors.qualification = "Qualification is required.";
    }

    // Experience
    if (!formData.experience.trim()) {
      newErrors.experience = "Experience is required.";
    }

    // Resume
    if (!formData.resume) {
      newErrors.resume = "Resume is required.";
    } else {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (!allowedTypes.includes(formData.resume.type)) {
        newErrors.resume = "Only PDF, DOC, or DOCX files are allowed.";
      }

      const maxSizeMB = 5;
      if (formData.resume.size > maxSizeMB * 1024 * 1024) {
        newErrors.resume = `Resume must be under ${maxSizeMB}MB.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      newErrors[name] = "";
      return newErrors;
    });

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors.resume;
      return newErrors;
    });

    setFormData((prev) => ({ ...prev, resume: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!validate()) {
      setLoading(false);
      return;
    }

    try {
      let resumeUrl = "";

      if (formData.resume) {
        const uploadResult = await uploadImageToCloudinary(formData.resume);
        if (uploadResult) {
          resumeUrl = typeof uploadResult === "string" ? uploadResult : uploadResult.url;
        } else {
          toast.error("Failed to upload resume. Please try again.");
          setLoading(false);
          return;
        }
      }

      const payload = {
        firstName: formData.fname,
        lastName: formData.lname,
        email: formData.email,
        phone: formData.number,
        role: formData.role,
        qualification: formData.qualification,
        experience: formData.experience,
        resumeUrl,
      };

      const APPLY_FORM_ENDPOINT = process.env.REACT_APP_APPLY_FORM_ENDPOINT || "";
      if (!APPLY_FORM_ENDPOINT) {
        toast.error("Apply form endpoint not configured in .env.");
        setLoading(false);
        return;
      }

      await fetch(APPLY_FORM_ENDPOINT, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain",
        },
        body: JSON.stringify(payload),
      });

      setFormData({
        fname: "",
        lname: "",
        email: "",
        number: "",
        role: "",
        qualification: "",
        experience: "",
        resume: null,
      });
      setErrors({});

      toast("✅ Application submitted!", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });

      navigator(-1)
    } catch (error: any) {
     toast.error(`${error.message}`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 md:pt-40 bg-darkGreyPrimary text-white flex flex-col gap-5 sm:gap-10 px-10 md:px-20 min-h-screen pb-10 justify-center">
      <div className="flex flex-col gap-1 max-w-[1980px] mx-auto w-full">
        <h1 className="text-3xl sm:text-4xl font-medium">Apply for the position:</h1>
        <h2 className="text-[22px] sm:text-2xl font-bold text-[#229CFF]">{location.state}</h2>
      </div>

      <form onSubmit={handleSubmit} className="max-w-[1980px] mx-auto w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <InputField
            id="fname"
            name="fname"
            label="First Name"
            placeholder="Enter your first name"
            value={formData.fname}
            onChange={handleChange}
            error={errors.fname}
          />

          <InputField
            id="lname"
            name="lname"
            label="Last Name"
            placeholder="Enter your last name"
            value={formData.lname}
            onChange={handleChange}
            error={errors.lname}
          />

          <InputField
            id="email"
            name="email"
            label="Email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
          />

          <InputField
            id="number"
            name="number"
            label="Phone Number"
            placeholder="Enter your phone number"
            value={formData.number}
            onChange={handleChange}
            error={errors.number}
          />
          <InputField
            id="role"
            name="role"
            label="Role"
            placeholder="Enter your phone number"
            value={formData.role}
            onChange={handleChange}
            error={errors.role}
            readOnly
            className="opacity-70 cursor-not-allowed"
          />

          {/* <SelectField
            id="role"
            name="role"
            label="Role"
            options={Roles}
            value={formData.role}
            onChange={handleChange}
            error={errors.role}
          /> */}

          <InputField
            id="qualification"
            name="qualification"
            label="Qualification"
            placeholder="Enter your qualification"
            value={formData.qualification}
            onChange={handleChange}
            error={errors.qualification}
          />

          <InputField
            id="experience"
            name="experience"
            label="Total Experience"
            placeholder="Enter your experience"
            value={formData.experience}
            onChange={handleChange}
            error={errors.experience}
          />

          <InputField
            id="resume"
            name="resume"
            type="file"
            label="Resume/CV (PDF/DOC only, max 5MB)"
            onChange={handleFileChange}
            style={{ colorScheme: "dark" }}
            error={errors.resume}
          />
        </div>

        <div className="flex justify-center mt-10">
          <ButtonComponent
            className="rounded-2xl flex gap-1 items-center shadow-custom-yellow bg-yellow text-white px-7 py-3 text-sm relative cursor-pointer disabled:opacity-50"
            labelClassname="text-base md:text-lg font-bold font-sans rounded-full mx-auto"
            label={loading ? "Submitting..." : "Submit"}
            loading={loading}
            //@ts-ignore
            type="submit"
          />
        </div>
      </form>
    </div>
  );
};

export default ApplyForm;
